import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import {
  AgendaMotionRow, AgendaMotionStats, Committee, CommitteeNewsArticle, ExpenseStats, FactionFunding, LobbyistDoc, LobbyistRegistry, MemberDoc,
  MemberIndexEntry, MinistryBudget, MinistryMeta, Party, PartyFundingDoc, QueryDoc, RegistryLobbyist, SecondaryLawRow, SecondaryLawStats,
} from '../types/types';

/* ── Shared, cached reads ─────────────────────────────────────────────────────
   One read per collection or doc per page load, shared by every page, so moving
   between pages (and StrictMode's double effects) never re-downloads. A failed read
   is dropped from the cache so the next call retries. Same pattern as
   hooks/useSchedule.ts. */

const cached = <T,>(load: () => Promise<T>) => {
  let promise: Promise<T> | null = null;
  return (): Promise<T> => {
    if (!promise) promise = load().catch((err: Error) => { promise = null; throw err; });
    return promise;
  };
};

/* ── Knessets ─────────────────────────────────────────────────────────────────
   meta/scope says which Knesset and government the live data describes. An earlier
   Knesset's lists are frozen under knessets/{N}/ at the same paths (--archive-knesset);
   its bills, queries, committees and parties stay in the live collections. */

export interface Scope {
  knesset: number;
  government: number;
  governmentTransitional: boolean;
  knessets: Record<string, { start: string; end: string | null }>;
  archivedKnessets: number[];
  lobbyistKnesset: number;
}

// Records written before they carried a Knesset number (party and committee docs, index
// entries) are the 25th Knesset's, whatever the current Knesset is
export const UNTAGGED_KNESSET = 25;

// Until meta/scope exists, or when it can't be read
export const DEFAULT_SCOPE: Scope = {
  knesset: 25, government: 37, governmentTransitional: false,
  knessets: { '25': { start: '2022-11-01T00:00:00Z', end: '2026-11-10T00:00:00Z' } },
  archivedKnessets: [], lobbyistKnesset: 25,
};

let scopePromise: Promise<Scope> | null = null;

// Never rejects: a failed read gives DEFAULT_SCOPE (and the next call retries)
export const getScope = (): Promise<Scope> => {
  if (!scopePromise) {
    scopePromise = getDoc(doc(db, 'meta', 'scope'))
      .then(snap => {
        if (!snap.exists()) return DEFAULT_SCOPE;
        const d = snap.data();
        return {
          knesset: typeof d.knesset === 'number' ? d.knesset : DEFAULT_SCOPE.knesset,
          government: typeof d.government === 'number' ? d.government : DEFAULT_SCOPE.government,
          governmentTransitional: d.governmentTransitional === true,
          knessets: (d.knessets || DEFAULT_SCOPE.knessets) as Scope['knessets'],
          archivedKnessets: Array.isArray(d.archivedKnessets) ? (d.archivedKnessets as number[]) : [],
          lobbyistKnesset: typeof d.lobbyistKnesset === 'number' ? d.lobbyistKnesset : DEFAULT_SCOPE.lobbyistKnesset,
        };
      })
      .catch(() => { scopePromise = null; return DEFAULT_SCOPE; });
  }
  return scopePromise;
};

// The Knesset whose term contains a date (an Israel-local or ISO date string)
export const knessetOn = (scope: Scope, date: string): number => {
  const day = (date || '').slice(0, 10);
  let found = scope.knesset;
  const ks = Object.keys(scope.knessets);
  for (let i = 0; i < ks.length; i++) {
    const t = scope.knessets[ks[i]];
    if (day >= t.start.slice(0, 10) && (!t.end || day < t.end.slice(0, 10))) found = Number(ks[i]);
  }
  return found;
};

// Knesset k's data is read from its archive (not the live paths)
const isArchivedKnesset = (scope: Scope, k: number | undefined): k is number =>
  typeof k === 'number' && k !== scope.knesset && scope.archivedKnessets.indexOf(k) !== -1;

// Path prefix of Knesset k's copy of a live path: '' for the current Knesset, 'knessets/{k}/'
// for an archived one
const prefixOf = async (k?: number): Promise<string> =>
  (k !== undefined && isArchivedKnesset(await getScope(), k) ? `knessets/${k}/` : '');

// A cache per path prefix (one per Knesset), same retry rule as cached()
const cachedByPrefix = <T,>(load: (prefix: string) => Promise<T>) => {
  const promises: Record<string, Promise<T>> = {};
  return (prefix: string): Promise<T> => {
    if (!Object.prototype.hasOwnProperty.call(promises, prefix)) {
      promises[prefix] = load(prefix).catch((err: Error) => { delete promises[prefix]; throw err; });
    }
    return promises[prefix];
  };
};

export const getCommittees = cached(async (): Promise<Committee[]> => {
  const snap = await getDocs(collection(db, 'committees'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Committee));
});

// The committee list for list pages: meta/committeeIndex (~62 KB, no sessions or lobbyist
// lists), falling back to the whole collection if the index doc is missing
export const getCommitteeList = cached(async (): Promise<Committee[]> => {
  const snap = await getDoc(doc(db, 'meta', 'committeeIndex'));
  if (!snap.exists()) return getCommittees();
  const raw = snap.data().committees;
  const parsed: Record<string, Omit<Committee, 'id'>> = typeof raw === 'string' ? JSON.parse(raw) : raw || {};
  return Object.keys(parsed).map(id => ({ id, ...parsed[id] } as Committee));
});

// The full committee doc (sessions, lobbyists, email). Resolves null if it doesn't exist.
const committeePromises: Record<string, Promise<Committee | null>> = {};
export const getCommittee = (id: string): Promise<Committee | null> => {
  if (!Object.prototype.hasOwnProperty.call(committeePromises, id)) {
    committeePromises[id] = getDoc(doc(db, 'committees', id))
      .then(snap => (snap.exists() ? ({ id: snap.id, ...snap.data() } as Committee) : null))
      .catch((err: Error) => { delete committeePromises[id]; throw err; });
  }
  return committeePromises[id];
};

// A committee's press releases (committeeNews/{committee doc ID}, newest first, ≤ 20);
// [] when it has none. No Knesset parameter: the docs are keyed by committee doc ID, which
// is new every Knesset, and are never deleted, so an earlier Knesset's committee keeps its
// releases at the live path (they aren't archived).
const newsPromises: Record<string, Promise<CommitteeNewsArticle[]>> = {};
export const getCommitteeNews = (committeeDocId: string): Promise<CommitteeNewsArticle[]> => {
  if (!Object.prototype.hasOwnProperty.call(newsPromises, committeeDocId)) {
    newsPromises[committeeDocId] = getDoc(doc(db, 'committeeNews', committeeDocId))
      .then(snap => (snap.exists() && Array.isArray(snap.data().articles) ? (snap.data().articles as CommitteeNewsArticle[]) : []))
      .catch((err: Error) => { delete newsPromises[committeeDocId]; throw err; });
  }
  return newsPromises[committeeDocId];
};

/* ── Parliamentary queries ── */

// Read-model docs keep their payload as a JSON string; accept either a string or an object
const parseJson = <T,>(raw: unknown, fallback: T): T => {
  if (typeof raw === 'string') return JSON.parse(raw) as T;
  return (raw as T) || fallback;
};

// The payload field of a list chunk ('queries', 'items' or 'list')
const chunkPayload = (data: Record<string, unknown>): unknown =>
  data.queries !== undefined ? data.queries : data.items !== undefined ? data.items : data.list;

let queriesPromise: Promise<QueryDoc[]> | null = null;
const archivedQueriesPromises: Record<number, Promise<QueryDoc[]>> = {};

// Knesset k's frozen query list (knessets/{k}/queryList chunks)
const getArchivedQueries = (k: number): Promise<QueryDoc[]> => {
  if (!Object.prototype.hasOwnProperty.call(archivedQueriesPromises, k)) {
    archivedQueriesPromises[k] = getDoc(doc(db, 'knessets', String(k), 'meta', 'queryListInfo'))
      .then(async info => {
        const n: number = info.exists() ? info.data().chunks || 0 : 0;
        const chunks = await Promise.all(Array.from({ length: n }, (_, i) => getDoc(doc(db, 'knessets', String(k), 'queryList', String(i)))));
        let list: QueryDoc[] = [];
        for (let i = 0; i < chunks.length; i++) {
          if (chunks[i].exists()) list = list.concat(parseJson<QueryDoc[]>(chunkPayload(chunks[i].data() || {}), []));
        }
        return list;
      })
      .catch((err: Error) => { delete archivedQueriesPromises[k]; throw err; });
  }
  return archivedQueriesPromises[k];
};

// All queries, newest first: the current Knesset's, or Knesset k's when it's archived.
// Reads the queryList chunks (meta/queryListInfo) when they exist, else the whole
// queries collection (~1,600 docs).
export const getQueries = async (k?: number): Promise<QueryDoc[]> => {
  if (k !== undefined && isArchivedKnesset(await getScope(), k)) return getArchivedQueries(k);
  if (!queriesPromise) {
    queriesPromise = getDoc(doc(db, 'meta', 'queryListInfo'))
      .then(async info => {
        if (info.exists() && info.data().chunks > 0) {
          const n: number = info.data().chunks;
          const chunks = await Promise.all(Array.from({ length: n }, (_, i) => getDoc(doc(db, 'queryList', String(i)))));
          let list: QueryDoc[] = [];
          for (let i = 0; i < chunks.length; i++) {
            if (chunks[i].exists()) list = list.concat(parseJson<QueryDoc[]>(chunkPayload(chunks[i].data() || {}), []));
          }
          return list;
        }
        const snap = await getDocs(collection(db, 'queries'));
        return snap.docs
          .map(d => ({ id: d.id, ...d.data() } as QueryDoc))
          .sort((a, b) => (b.submitDate || '').localeCompare(a.submitDate || ''));
      })
      .catch((err: Error) => { queriesPromise = null; throw err; });
  }
  return queriesPromise;
};

// One full query doc (the list has no document URLs)
const queryPromises: Record<string, Promise<QueryDoc | null>> = {};
export const getQuery = (id: string): Promise<QueryDoc | null> => {
  if (!Object.prototype.hasOwnProperty.call(queryPromises, id)) {
    queryPromises[id] = getDoc(doc(db, 'queries', id))
      .then(snap => (snap.exists() ? ({ id: snap.id, ...snap.data() } as QueryDoc) : null))
      .catch((err: Error) => { delete queryPromises[id]; throw err; });
  }
  return queryPromises[id];
};

/* ── Government ── */

export interface GovBillStats { total: number; passed: number; inProgress: number }

// meta/ministries (~24 KB): per-ministry name, minister, query stats; plus government bill stats
export const getMinistriesDoc = cached(async (): Promise<{ byId: Record<string, MinistryMeta>; govBillStats: GovBillStats | null }> => {
  const snap = await getDoc(doc(db, 'meta', 'ministries'));
  if (!snap.exists()) return { byId: {}, govBillStats: null };
  const d = snap.data();
  const g = d.govBillStats;
  return {
    byId: (d.byId || {}) as Record<string, MinistryMeta>,
    govBillStats: g ? { total: g.total || 0, passed: g.passed || 0, inProgress: g.inProgress || 0 } : null,
  };
});

export const getMinistries = (): Promise<Record<string, MinistryMeta>> => getMinistriesDoc().then(d => d.byId);

// meta/budget (~44 KB): yearly budget and grade per budget code, and ministry name → code
export const getBudget = cached(async (): Promise<{ nameToCode: Record<string, string>; byCode: Record<string, MinistryBudget> }> => {
  const snap = await getDoc(doc(db, 'meta', 'budget'));
  if (!snap.exists()) return { nameToCode: {}, byCode: {} };
  const raw = (snap.data().byCode || {}) as Record<string, { years?: MinistryBudget['years']; grade?: MinistryBudget['grade'] }>;
  const byCode: Record<string, MinistryBudget> = {};
  const codes = Object.keys(raw);
  for (let i = 0; i < codes.length; i++) byCode[codes[i]] = { years: raw[codes[i]].years || {}, grade: raw[codes[i]].grade || null };
  return { nameToCode: (snap.data().nameToCode || {}) as Record<string, string>, byCode };
});

// A ministry's budget: its own `budget` field, else looked up in meta/budget by name or code
export const getMinistryBudget = async (m: MinistryMeta): Promise<MinistryBudget | null> => {
  if (m.budget && Object.keys(m.budget.years || {}).length > 0) return m.budget;
  const { nameToCode, byCode } = await getBudget();
  const code = nameToCode[(m.name || '').trim().replace(/\s+/g, ' ')] || m.budgetCode;
  return code ? byCode[code] || null : null;
};

// One full bill doc (votes, co-initiators, documents). Resolves null if it doesn't exist.
const billPromises: Record<string, Promise<Record<string, unknown> & { id: string } | null>> = {};
export const getBill = (id: string) => {
  if (!Object.prototype.hasOwnProperty.call(billPromises, id)) {
    billPromises[id] = getDoc(doc(db, 'bills', id))
      .then(snap => (snap.exists() ? { id: snap.id, ...snap.data() } : null))
      .catch((err: Error) => { delete billPromises[id]; throw err; });
  }
  return billPromises[id];
};

export interface GovBill { id: string; title: string; simpleStage?: string; lastUpdated?: string }

// Newest (lastUpdated) first; equal dates by doc ID, the order Firestore returns docs in
const byLastUpdated = (a: GovBill, b: GovBill): number =>
  (b.lastUpdated || '').localeCompare(a.lastUpdated || '') || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

// The current Knesset's government bills (~630), newest first; only the government page's
// bills tab needs them. Filtered from the billList chunks, which are cached and shared with
// the legislation page, so no full bill doc (votes, documents, timeline) is downloaded.
// The list is the current Knesset's once meta/billStats (written with it) says so; until
// then, just after a new Knesset starts, there are none, as with the bill docs.
export const getGovernmentBills = cached(async (): Promise<GovBill[]> => {
  const [scope, info] = await Promise.all([getScope(), getBillListInfo('')]);
  if (!info) {
    // No billList yet: the government bill docs themselves
    const snap = await getDocs(query(collection(db, 'bills'), where('billType', '==', 'government')));
    return snap.docs
      .filter(d => (d.data().knessetNumber ?? UNTAGGED_KNESSET) === scope.knesset)
      .map(d => ({ id: d.id, title: d.data().title || '', simpleStage: d.data().simpleStage, lastUpdated: toIso(d.data().lastUpdated) }))
      .sort(byLastUpdated);
  }
  const [stats, all] = await Promise.all([loadBillStats(''), getAllBills()]);
  if (((stats && stats.knessetNumber) ?? UNTAGGED_KNESSET) !== scope.knesset) return [];
  const list: GovBill[] = [];
  for (let i = 0; i < all.length; i++) {
    const b = all[i];
    if (b.billType === 'government') list.push({ id: b.id, title: b.title || '', simpleStage: b.simpleStage, lastUpdated: b.lastUpdated });
  }
  return list.sort(byLastUpdated);
});

// lastUpdated is stored as an ISO string or a Firestore timestamp, depending on the doc
const toIso = (v: unknown): string | undefined => {
  if (!v) return undefined;
  if (typeof v === 'string') return v;
  const ts = v as { toDate?: () => Date };
  return ts.toDate ? ts.toDate().toISOString() : undefined;
};

// Every party of every Knesset (13 docs per Knesset), for lookups by ID
export const getParties = cached(async (): Promise<Party[]> => {
  const snap = await getDocs(collection(db, 'parties'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Party));
});

// Knesset k's parties
export const getPartiesOf = async (k: number): Promise<Party[]> => {
  const parties = await getParties();
  return parties.filter(p => (p.knessetNumber ?? UNTAGGED_KNESSET) === k);
};

// The current Knesset's parties, for lists, seat counts and search
export const getCurrentParties = async (): Promise<Party[]> => getPartiesOf((await getScope()).knesset);

/* ── Lobbyists, per Knesset ─────────────────────────────────────────────────────
   The live lobbyists are the current Knesset's; an archived Knesset's are under
   knessets/{k}/ (lobbyists/{id} and meta/lobbyistIndex), which the sync keeps up to date with
   that Knesset's late protocols for a few months after it ends. A lobbyist's doc ID (their
   name) is the same in every Knesset. */

// One full lobbyist doc (memberIds and per-committee organizations, which the index lacks)
const lobbyistPromises: Record<string, Promise<LobbyistDoc | null>> = {};
const lobbyistAt = (prefix: string, id: string): Promise<LobbyistDoc | null> => {
  const key = `${prefix}${id}`;
  if (!Object.prototype.hasOwnProperty.call(lobbyistPromises, key)) {
    lobbyistPromises[key] = getDoc(doc(db, `${prefix}lobbyists`, id))
      .then(snap => (snap.exists() ? ({ id: snap.id, ...snap.data() } as LobbyistDoc) : null))
      .catch((err: Error) => { delete lobbyistPromises[key]; throw err; });
  }
  return lobbyistPromises[key];
};
// Knesset k's doc of a lobbyist (the current Knesset's when k is omitted); null if they have none
export const getLobbyistOf = async (k: number | undefined, id: string): Promise<LobbyistDoc | null> => lobbyistAt(await prefixOf(k), id);
export const getLobbyist = (id: string): Promise<LobbyistDoc | null> => lobbyistAt('', id);

// Every Knesset a lobbyist appeared in, with that Knesset's doc: the current one first, then
// the archived ones, newest first. [] when none has them.
export const getLobbyistKnessets = async (id: string): Promise<{ k: number; doc: LobbyistDoc }[]> => {
  const scope = await getScope();
  const ks = [scope.knesset].concat(scope.archivedKnessets.filter(k => k !== scope.knesset).sort((a, b) => b - a));
  const docs = await Promise.all(ks.map(k => getLobbyistOf(k, id)));
  const out: { k: number; doc: LobbyistDoc }[] = [];
  for (let i = 0; i < ks.length; i++) { const d = docs[i]; if (d) out.push({ k: ks[i], doc: d }); }
  return out;
};

// Knesset k's lobbyists (the current Knesset's when k is omitted): its lobbyist index when it
// exists (one read), else the collection
const lobbyistsAt = cachedByPrefix(async (prefix: string): Promise<LobbyistDoc[]> => {
  const index = await getDoc(doc(db, `${prefix}meta`, 'lobbyistIndex'));
  if (index.exists() && index.data().lobbyists !== undefined) {
    const parsed = parseJson<LobbyistDoc[] | Record<string, Omit<LobbyistDoc, 'id'>>>(index.data().lobbyists, []);
    if (Array.isArray(parsed)) return parsed;
    return Object.keys(parsed).map(id => ({ id, ...parsed[id] } as LobbyistDoc));
  }
  const snap = await getDocs(collection(db, `${prefix}lobbyists`));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as LobbyistDoc));
});
export const getLobbyistsOf = async (k?: number): Promise<LobbyistDoc[]> => lobbyistsAt(await prefixOf(k));
export const getLobbyists = (): Promise<LobbyistDoc[]> => lobbyistsAt('');

/* ── Lobbyist register (meta/lobbyistRegistry, ~180 KB, one read) ──────────────
   The Knesset's register of licensed lobbyists: permit, firm, declared party and clients.
   The current register (not a Knesset's), so it has no `k` and is never archived. */

export interface LobbyistRegistryIndex extends LobbyistRegistry {
  // Our lobbyists/{id} doc → its register entry (only the lobbyists the sync matched)
  byDocId: Record<string, RegistryLobbyist>;
  // Client name, exactly as registered → the registryIds of the lobbyists who list it
  clients: Record<string, number[]>;
}

// Resolves null when the doc doesn't exist yet
export const getLobbyistRegistry = cached(async (): Promise<LobbyistRegistryIndex | null> => {
  const snap = await getDoc(doc(db, 'meta', 'lobbyistRegistry'));
  if (!snap.exists()) return null;
  const d = snap.data();
  const raw = parseJson<Record<string, Omit<RegistryLobbyist, 'registryId'>>>(d.lobbyists, {});
  const lobbyists: Record<string, RegistryLobbyist> = {};
  const byDocId: Record<string, RegistryLobbyist> = {};
  const clients: Record<string, number[]> = {};
  const ids = Object.keys(raw);
  for (let i = 0; i < ids.length; i++) {
    const l: RegistryLobbyist = { ...raw[ids[i]], registryId: Number(ids[i]), clients: raw[ids[i]].clients || [] };
    lobbyists[ids[i]] = l;
    if (l.docId) byDocId[l.docId] = l;
    for (let j = 0; j < l.clients.length; j++) {
      const name = l.clients[j].name;
      if (!Object.prototype.hasOwnProperty.call(clients, name)) clients[name] = [];
      if (clients[name].indexOf(l.registryId) === -1) clients[name].push(l.registryId);
    }
  }
  return {
    lobbyists, byDocId, clients,
    count: typeof d.count === 'number' ? d.count : ids.length,
    clientCount: typeof d.clientCount === 'number' ? d.clientCount : Object.keys(clients).length,
    matched: typeof d.matched === 'number' ? d.matched : Object.keys(byDocId).length,
    asOf: typeof d.asOf === 'string' ? d.asOf : null,
  };
});

// One firm and the lobbyists of it who represent a client. Independent lobbyists share one
// group with firm null.
export interface ClientFirmGroup { firm: string | null; lobbyists: RegistryLobbyist[] }

// Who represents a client (its exact registered name), grouped by firm: firms א–ת, then the
// independent lobbyists; lobbyists by name within a group. [] for a name nobody lists.
export const clientRepresentatives = (reg: LobbyistRegistryIndex, clientName: string): ClientFirmGroup[] => {
  const ids = Object.prototype.hasOwnProperty.call(reg.clients, clientName) ? reg.clients[clientName] : [];
  const byFirm: Record<string, RegistryLobbyist[]> = {};
  const independents: RegistryLobbyist[] = [];
  for (let i = 0; i < ids.length; i++) {
    const l = reg.lobbyists[String(ids[i])];
    if (!l) continue;
    if (!l.firm) { independents.push(l); continue; }
    if (!Object.prototype.hasOwnProperty.call(byFirm, l.firm)) byFirm[l.firm] = [];
    byFirm[l.firm].push(l);
  }
  const byName = (a: RegistryLobbyist, b: RegistryLobbyist) => a.fullName.localeCompare(b.fullName, 'he');
  const groups: ClientFirmGroup[] = Object.keys(byFirm)
    .sort((a, b) => a.localeCompare(b, 'he'))
    .map(firm => ({ firm, lobbyists: byFirm[firm].sort(byName) }));
  if (independents.length > 0) groups.push({ firm: null, lobbyists: independents.sort(byName) });
  return groups;
};

/* ── Members ──────────────────────────────────────────────────────────────────
   meta/memberIndex holds the light fields of every member (~200 KB of JSON, one read).
   If it is missing or lacks an ID, getMemberSummaries() falls back to reading those
   member docs once each (deduplicated and cached per ID). */

// Resolves null when the index doc doesn't exist yet
// Per party: unique lobbyists who appeared before committees its MKs chair
export interface PartyChairLobbyists { lobbyists: number; appearances: number; committees: number }

export interface MemberIndexDoc { members: Record<string, MemberIndexEntry>; partyChairLobbyists: Record<string, PartyChairLobbyists> }

const parseMemberIndex = (data: Record<string, unknown>): MemberIndexDoc => {
  const raw = data.members;
  const parsed: Record<string, Omit<MemberIndexEntry, 'id'>> = typeof raw === 'string' ? JSON.parse(raw) : (raw as Record<string, Omit<MemberIndexEntry, 'id'>>) || {};
  const members: Record<string, MemberIndexEntry> = {};
  const ids = Object.keys(parsed);
  for (let i = 0; i < ids.length; i++) members[ids[i]] = { id: ids[i], ...parsed[ids[i]] };
  return { members, partyChairLobbyists: (data.partyChairLobbyists || {}) as Record<string, PartyChairLobbyists> };
};

const getMemberIndexDoc = cached(async (): Promise<MemberIndexDoc | null> => {
  const snap = await getDoc(doc(db, 'meta', 'memberIndex'));
  return snap.exists() ? parseMemberIndex(snap.data()) : null;
});

// Knesset k's member index: the live one for the current Knesset, the frozen copy for an
// archived one (members as they were then: party, roles, stats). Resolves null if missing.
const archivedIndexPromises: Record<number, Promise<MemberIndexDoc | null>> = {};
export const getMemberIndexDocOf = async (k: number | undefined): Promise<MemberIndexDoc | null> => {
  const scope = await getScope();
  if (!isArchivedKnesset(scope, k)) return getMemberIndexDoc();
  if (!Object.prototype.hasOwnProperty.call(archivedIndexPromises, k)) {
    archivedIndexPromises[k] = getDoc(doc(db, 'knessets', String(k), 'meta', 'memberIndex'))
      .then(snap => (snap.exists() ? parseMemberIndex(snap.data()) : null))
      .catch((err: Error) => { delete archivedIndexPromises[k]; throw err; });
  }
  return archivedIndexPromises[k];
};

// A member's record of an archived Knesset (knessets/{k}/members/{id}: party, roles, bill and
// vote stats, committees as they were at its end). Resolves null when there's none.
export interface MemberKnessetRecord {
  partyId?: string | null;
  knessetNumber?: number;
  billStats?: MemberDoc['billStats'];
  proposedBills?: MemberDoc['proposedBills'];
  voteStats?: MemberDoc['voteStats'];
  billVotes?: MemberDoc['billVotes'];
  queryCount?: number;
  agendaMotionCount?: number;
  committeeIds?: string[];
  substituteCommitteeIds?: string[];
  lobbyistCount?: number;
  roles?: MemberDoc['roles'];
}
const knessetRecordPromises: Record<string, Promise<MemberKnessetRecord | null>> = {};
export const getMemberKnessetRecord = (id: string, k: number): Promise<MemberKnessetRecord | null> => {
  const key = `${k}/${id}`;
  if (!Object.prototype.hasOwnProperty.call(knessetRecordPromises, key)) {
    knessetRecordPromises[key] = getDoc(doc(db, 'knessets', String(k), 'members', id))
      .then(snap => (snap.exists() ? (snap.data() as MemberKnessetRecord) : null))
      .catch((err: Error) => { delete knessetRecordPromises[key]; throw err; });
  }
  return knessetRecordPromises[key];
};

// Every member's light fields as Knesset k saw them (its archived index), else the live list
export const getAllMembersOf = async (k: number | undefined): Promise<MemberIndexEntry[]> => {
  if (!isArchivedKnesset(await getScope(), k)) return getAllMembers();
  const d = await getMemberIndexDocOf(k);
  return d ? Object.keys(d.members).map(id => d.members[id]) : [];
};

// Per party of Knesset k: lobbying before the committees its MKs chaired
export const getPartyChairLobbyistsOf = (k: number | undefined): Promise<Record<string, PartyChairLobbyists>> =>
  getMemberIndexDocOf(k).then(d => (d ? d.partyChairLobbyists : {}));

// Resolves null when the index doc doesn't exist yet
export const getMemberIndex = (): Promise<Record<string, MemberIndexEntry> | null> => getMemberIndexDoc().then(d => (d ? d.members : null));

export const getPartyChairLobbyists = (): Promise<Record<string, PartyChairLobbyists>> =>
  getMemberIndexDoc().then(d => (d ? d.partyChairLobbyists : {}));

// Expense details (yearly totals, categories, items) of every member doc of a party, for
// the party page's expense breakdown. Full member docs are large, so this loads only on demand.
export interface MemberExpenses {
  id: string;
  name: string;
  isCurrentMk?: boolean;
  isActiveMk?: boolean;
  governmentMember?: boolean;
  expenses?: Record<string, { total: number; byCategory: Record<string, number>; items?: Array<{ category: string; vendor: string; date: string; amount: number }> }>;
}
const partyExpensePromises: Record<string, Promise<MemberExpenses[]>> = {};
export const getPartyMemberExpenses = (partyId: string): Promise<MemberExpenses[]> => {
  if (!Object.prototype.hasOwnProperty.call(partyExpensePromises, partyId)) {
    partyExpensePromises[partyId] = getDocs(query(collection(db, 'members'), where('partyId', '==', partyId)))
      .then(snap => snap.docs.map(d => {
        const x = d.data();
        return { id: d.id, name: x.name || '', isCurrentMk: x.isCurrentMk, isActiveMk: x.isActiveMk, governmentMember: x.governmentMember, expenses: x.expenses } as MemberExpenses;
      }))
      .catch((err: Error) => { delete partyExpensePromises[partyId]; throw err; });
  }
  return partyExpensePromises[partyId];
};

const memberDocPromises: Record<string, Promise<MemberIndexEntry | null>> = {};

const getMemberFromDoc = (id: string): Promise<MemberIndexEntry | null> => {
  if (!Object.prototype.hasOwnProperty.call(memberDocPromises, id)) {
    memberDocPromises[id] = getDoc(doc(db, 'members', id))
      .then(snap => {
        if (!snap.exists()) return null;
        const d = snap.data();
        return { id, name: d.name || '', photoUrl: d.photoUrl, imageUrl: d.imageUrl, thumbUrl: d.thumbUrl, thumbUrl240: d.thumbUrl240, partyId: d.partyId } as MemberIndexEntry;
      })
      .catch((err: Error) => { delete memberDocPromises[id]; throw err; });
  }
  return memberDocPromises[id];
};

// Name, photo and party (and, from the index, the other light fields) for the given IDs
export const getMemberSummaries = async (ids: string[]): Promise<Record<string, MemberIndexEntry>> => {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  const out: Record<string, MemberIndexEntry> = {};
  let index: Record<string, MemberIndexEntry> | null = null;
  try { index = await getMemberIndex(); } catch { index = null; }

  const missing: string[] = [];
  for (let i = 0; i < unique.length; i++) {
    const hit = index ? index[unique[i]] : undefined;
    if (hit) out[unique[i]] = hit; else missing.push(unique[i]);
  }
  if (missing.length > 0) {
    const docs = await Promise.all(missing.map(getMemberFromDoc));
    for (let i = 0; i < docs.length; i++) {
      const m = docs[i];
      if (m) out[m.id] = m;
    }
  }
  return out;
};

// One full member doc (bio, career, bills, votes and expenses; 100–500 KB), for the MK
// page. Resolves null if it doesn't exist.
const fullMemberPromises: Record<string, Promise<MemberDoc | null>> = {};
export const getMember = (id: string): Promise<MemberDoc | null> => {
  if (!Object.prototype.hasOwnProperty.call(fullMemberPromises, id)) {
    fullMemberPromises[id] = getDoc(doc(db, 'members', id))
      .then(snap => (snap.exists() ? ({ id: snap.id, ...snap.data() } as MemberDoc) : null))
      .catch((err: Error) => { delete fullMemberPromises[id]; throw err; });
  }
  return fullMemberPromises[id];
};

// meta/expenseStats: per-MK rankings and party averages (--expenses)
export const getExpenseStats = cached(async (): Promise<ExpenseStats | null> => {
  const snap = await getDoc(doc(db, 'meta', 'expenseStats'));
  return snap.exists() ? (snap.data() as ExpenseStats) : null;
});

// Which MKs paid a vendor, and how much each (vendors/{id}). Only vendors used by 2+ MKs
// or totalling ₪50K have a doc; the others resolve null.
export interface VendorCrossRef { mkIds: string[]; byMk: Record<string, number> }
const vendorPromises: Record<string, Promise<VendorCrossRef | null>> = {};
export const getVendor = (vendorName: string): Promise<VendorCrossRef | null> => {
  // The doc ID is the vendor name with slashes, dots and spaces as underscores (--expenses)
  const id = vendorName.trim().replace(/\s+/g, ' ').replace(/[/\\.\s]+/g, '_').substring(0, 100);
  if (!id) return Promise.resolve(null);
  if (!Object.prototype.hasOwnProperty.call(vendorPromises, id)) {
    vendorPromises[id] = getDoc(doc(db, 'vendors', id))
      .then(snap => {
        if (!snap.exists()) return null;
        const d = snap.data();
        return { mkIds: (d.mkIds || []) as string[], byMk: (d.byMk || {}) as Record<string, number> };
      })
      .catch((err: Error) => { delete vendorPromises[id]; throw err; });
  }
  return vendorPromises[id];
};

// Every vendors/{id} doc (about 1,270), largest first. Only for "show all vendors".
export interface VendorSummary extends VendorCrossRef { name: string; totalSpending: number; mkCount: number }
export const getAllVendors = cached(async (): Promise<VendorSummary[]> => {
  const snap = await getDocs(collection(db, 'vendors'));
  return snap.docs
    .map(d => {
      const x = d.data();
      return { name: x.name || d.id, totalSpending: x.totalSpending || 0, mkCount: x.mkCount || 0, mkIds: (x.mkIds || []) as string[], byMk: (x.byMk || {}) as Record<string, number> };
    })
    .sort((a, b) => b.totalSpending - a.totalSpending);
});

// Every member's light fields: the member index, else the whole members collection
// (mapped to the same shape). For pages that list members by role or party.
export const getAllMembers = cached(async (): Promise<MemberIndexEntry[]> => {
  let index: Record<string, MemberIndexEntry> | null = null;
  try { index = await getMemberIndex(); } catch { index = null; }
  if (index) return Object.keys(index).map(id => index![id]);
  const snap = await getDocs(collection(db, 'members'));
  return snap.docs.map(d => {
    const x = d.data();
    return {
      id: d.id, name: x.name || '', photoUrl: x.photoUrl, imageUrl: x.imageUrl, thumbUrl: x.thumbUrl, thumbUrl240: x.thumbUrl240,
      partyId: x.partyId, role: x.role, roles: x.roles, gender: x.gender, isCurrentMk: x.isCurrentMk,
      isActiveMk: x.isActiveMk, governmentMember: x.governmentMember,
    } as MemberIndexEntry;
  });
});

/* ── Bill list (billList/{n} chunks, newest first) ───────────────────────────
   Each chunk holds ~1,000 light bill entries as a JSON string. The legislation page
   shows chunk 0 at once and loads the rest only for search, filters, other sorts or
   paging past it. Without meta/billListInfo it falls back to the whole collection. */

export interface BillListEntry {
  id: string;
  title: string;
  simpleStage?: string;
  billType?: string;
  billClass?: string;
  isBasicLaw?: boolean;
  topics?: string[];
  lastUpdated?: string;
  proposerId?: string;
}

export interface BillStatsDoc {
  total: number;
  byStage: Record<string, number>;
  byType: Record<string, number>;
  byTopic: Record<string, number>;
  // The Knesset these bills belong to (stats written before it was recorded are K25's)
  knessetNumber?: number;
}

// meta/billStats (~1 KB): totals per stage, type and topic; Knesset k's frozen copy for an
// archived Knesset
const loadBillStats = cachedByPrefix(async (prefix: string): Promise<BillStatsDoc | null> => {
  const snap = await getDoc(doc(db, `${prefix}meta/billStats`));
  if (!snap.exists()) return null;
  const d = snap.data();
  return {
    total: d.total || 0, byStage: d.byStage || {}, byType: d.byType || {}, byTopic: d.byTopic || {},
    knessetNumber: typeof d.knessetNumber === 'number' ? d.knessetNumber : undefined,
  };
});
export const getBillStats = async (k?: number): Promise<BillStatsDoc | null> => loadBillStats(await prefixOf(k));

const getBillListInfo = cachedByPrefix(async (prefix: string): Promise<{ chunks: number; total: number } | null> => {
  const snap = await getDoc(doc(db, `${prefix}meta/billListInfo`));
  return snap.exists() && snap.data().chunks > 0 ? { chunks: snap.data().chunks, total: snap.data().total || 0 } : null;
});

const billChunkPromises: Record<string, Promise<BillListEntry[]>> = {};
const getBillListChunk = (n: number, prefix: string): Promise<BillListEntry[]> => {
  const key = `${prefix}${n}`;
  if (!Object.prototype.hasOwnProperty.call(billChunkPromises, key)) {
    billChunkPromises[key] = getDoc(doc(db, `${prefix}billList/${n}`))
      .then(snap => (snap.exists() ? parseJson<BillListEntry[]>(snap.data().bills, []) : []))
      .catch((err: Error) => { delete billChunkPromises[key]; throw err; });
  }
  return billChunkPromises[key];
};

const getBillsFromCollection = cached(async (): Promise<BillListEntry[]> => {
  const snap = await getDocs(collection(db, 'bills'));
  return snap.docs
    .map(d => {
      const x = d.data();
      return { id: d.id, title: x.title || '', simpleStage: x.simpleStage, billType: x.billType, billClass: x.billClass, isBasicLaw: x.isBasicLaw, topics: x.topics, lastUpdated: toIso(x.lastUpdated), proposerId: x.proposerId } as BillListEntry;
    })
    .sort((a, b) => (b.lastUpdated || '').localeCompare(a.lastUpdated || ''));
});

// The newest ~1,000 bills; `complete` says whether that is already every bill. `k`: an
// archived Knesset's frozen list instead of the current one.
export const getRecentBills = async (k?: number): Promise<{ bills: BillListEntry[]; complete: boolean }> => {
  const prefix = await prefixOf(k);
  const info = await getBillListInfo(prefix);
  if (!info) return { bills: prefix ? [] : await getBillsFromCollection(), complete: true };
  return { bills: await getBillListChunk(0, prefix), complete: info.chunks === 1 };
};

// Every bill, newest first (`k` as above)
export const getAllBills = async (k?: number): Promise<BillListEntry[]> => {
  const prefix = await prefixOf(k);
  const info = await getBillListInfo(prefix);
  if (!info) return prefix ? [] : getBillsFromCollection();
  const chunks = await Promise.all(Array.from({ length: info.chunks }, (_, i) => getBillListChunk(i, prefix)));
  let all: BillListEntry[] = [];
  for (let i = 0; i < chunks.length; i++) all = all.concat(chunks[i]);
  return all;
};

/* ── One Knesset's chunked lists: secondary legislation and agenda motions ─────
   `{collection}/{n}` chunks of 1,000 rows (JSON in `items`) plus meta/{collection}Info,
   written whole by their sync steps, so a page reads every chunk at once. `k`: Knesset k's
   frozen copy under knessets/{k}/ when k is archived, else the live list, which describes
   the Knesset in the stats doc's knessetNumber. A committee page passes its committee's
   Knesset (`committee.knessetNumber ?? UNTAGGED_KNESSET`); the rows name committee docs, so
   a committee of a Knesset whose list isn't there simply finds none. Lists that don't
   exist yet (the step never ran, or an archive made without them) resolve []. */

// Every row of `{prefix}{name}/{n}` for n below meta/{name}Info.chunks, in stored order
const readChunkedList = async <T,>(prefix: string, name: string): Promise<T[]> => {
  const info = await getDoc(doc(db, `${prefix}meta/${name}Info`));
  const n: number = info.exists() ? info.data().chunks || 0 : 0;
  const chunks = await Promise.all(Array.from({ length: n }, (_, i) => getDoc(doc(db, `${prefix}${name}/${i}`))));
  let list: T[] = [];
  for (let i = 0; i < chunks.length; i++) {
    if (chunks[i].exists()) list = list.concat(parseJson<T[]>(chunkPayload(chunks[i].data() || {}), []));
  }
  return list;
};

const loadSecondaryLaws = cachedByPrefix((prefix: string) => readChunkedList<SecondaryLawRow>(prefix, 'secondaryLawList'));

// Every secondary-legislation item of Knesset k (default: the current one), newest
// received first (~2,800 rows in K25: 3 chunks of about 600 KB)
export const getSecondaryLaws = async (k?: number): Promise<SecondaryLawRow[]> => loadSecondaryLaws(await prefixOf(k));

const loadSecondaryLawStats = cachedByPrefix(async (prefix: string): Promise<SecondaryLawStats | null> => {
  const snap = await getDoc(doc(db, `${prefix}meta/secondaryLawStats`));
  if (!snap.exists()) return null;
  const d = snap.data();
  return {
    total: d.total || 0,
    byKind: d.byKind || {},
    byStatus: d.byStatus || {},
    byCommittee: d.byCommittee || {},
    byMinistryCategory: d.byMinistryCategory || {},
    knessetNumber: typeof d.knessetNumber === 'number' ? d.knessetNumber : undefined,
    lastSynced: typeof d.lastSynced === 'string' ? d.lastSynced : undefined,
  };
});

// meta/secondaryLawStats (~2 KB): totals by kind and status, and per committee and ministry
// category, so a tab can show its count (or hide itself) without the list. Null if missing.
export const getSecondaryLawStats = async (k?: number): Promise<SecondaryLawStats | null> => loadSecondaryLawStats(await prefixOf(k));

const loadAgendaMotions = cachedByPrefix((prefix: string) => readChunkedList<AgendaMotionRow>(prefix, 'agendaMotionList'));

// Every motion for the agenda of Knesset k (default: the current one), newest first (~800 in K25)
export const getAgendaMotions = async (k?: number): Promise<AgendaMotionRow[]> => loadAgendaMotions(await prefixOf(k));

const loadAgendaMotionStats = cachedByPrefix(async (prefix: string): Promise<AgendaMotionStats | null> => {
  const snap = await getDoc(doc(db, `${prefix}meta/agendaMotionStats`));
  if (!snap.exists()) return null;
  const d = snap.data();
  return {
    total: d.total || 0,
    combined: d.combined || 0,
    byType: d.byType || {},
    byStatus: d.byStatus || {},
    knessetNumber: typeof d.knessetNumber === 'number' ? d.knessetNumber : undefined,
    lastSynced: typeof d.lastSynced === 'string' ? d.lastSynced : undefined,
  };
});

// meta/agendaMotionStats: totals by type and status. Null if missing.
export const getAgendaMotionStats = async (k?: number): Promise<AgendaMotionStats | null> => loadAgendaMotionStats(await prefixOf(k));

/* ── Party funding (meta/partyFunding, ~75 KB, one read; --party-funding) ─────────
   Per faction: donations, guarantees and loans reported to the State Comptroller, and the
   monthly state funding by the formula. `k`: an archived Knesset's copy (its factions). */

const loadPartyFunding = cachedByPrefix(async (prefix: string): Promise<PartyFundingDoc | null> => {
  const snap = await getDoc(doc(db, `${prefix}meta/partyFunding`));
  if (!snap.exists()) return null;
  const d = snap.data();
  const electionDays: Record<string, string> = d.electionDays || {};
  const sf = d.stateFunding || {};
  // Docs written before `campaign` existed: the campaign after the last known election
  const known = Object.keys(electionDays).map(Number);
  const campaign = typeof d.campaign === 'number' ? d.campaign : (known.length ? Math.max.apply(null, known) + 1 : 0);
  return {
    factions: parseJson<Record<string, FactionFunding>>(d.factions, {}),
    campaign,
    electionDays,
    unit: typeof sf.unit === 'number' ? sf.unit : 0,
    unitFrom: typeof sf.unitFrom === 'string' ? sf.unitFrom : '',
    monthlyShare: typeof sf.monthlyShare === 'number' ? sf.monthlyShare : 0.06,
    election: sf.election || {},
    checkedAt: typeof d.checkedAt === 'string' ? d.checkedAt : null,
  };
});

// Null when the doc doesn't exist (the step never ran, or an archive made before it)
export const getPartyFunding = async (k?: number): Promise<PartyFundingDoc | null> => loadPartyFunding(await prefixOf(k));
